import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { ClientOrderType } from "@threadheads/shared";

interface ClientOrderTypeBadgeProps {
  type: ClientOrderType;
  className?: string;
}

/** Outline badge per fulfilment type, hues from the approved Layout C mockup
 *  (POD blue / PAP emerald / Mixed violet). Text label always visible —
 *  colour is never the only signal (DESIGN.md §16). */
const TYPE_CLASS: Record<ClientOrderType, string> = {
  pod: "border-blue-300 bg-blue-50 text-blue-700",
  pap: "border-emerald-300 bg-emerald-50 text-emerald-700",
  mixed: "border-violet-300 bg-violet-50 text-violet-700",
};

/**
 * BLU-665 Phase 2 — POD / PAP / Mixed order-type badge, shown on inbox cards
 * and the detail-pane header. Derived server-side from the order's
 * total_pod_items / total_pap_items counters; callers must omit rendering
 * when `orderType` is null (counters unpopulated).
 */
export function ClientOrderTypeBadge({
  type,
  className,
}: ClientOrderTypeBadgeProps) {
  const { t } = useTranslation("clientOrders");

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-[5px] border px-1.5 py-px text-[11px] font-semibold",
        TYPE_CLASS[type],
        className,
      )}
    >
      {t(`type.${type}`)}
    </span>
  );
}
